import React, { useEffect, useState } from 'react';
import { Outlet, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, X } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { useAuthStore } from '../../store/auth';
import { ToastProvider } from '../ui/Toast';

// What a restreamer's login may open: its portal and its own account pages.
// Every other page of the panel is the operator's, and a page that forgot its
// own role guard must not become reachable by typing its address.
function restreamerMayOpen(path: string): boolean {
  return path === '/portal' || path.startsWith('/portal/') || path === '/settings/2fa' || path === '/change-password';
}

export function Shell() {
  const { token, user, impersonating, exitImpersonation } = useAuthStore();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Below the lg breakpoint the sidebar is a drawer; it closes on navigation
  // and on Escape.
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => { setNavOpen(false); }, [pathname]);
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setNavOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);

  if (!token) {
    return <Navigate to="/login" replace />;
  }
  if (user?.role === 'restreamer' && !restreamerMayOpen(pathname)) {
    return <Navigate to="/portal" replace />;
  }

  const handleExitImpersonation = () => {
    exitImpersonation();
    navigate('/clients');
  };

  return (
    <ToastProvider>
      <div className="flex h-screen overflow-hidden bg-gray-50 dark:bg-slate-900">
        <Sidebar open={navOpen} onClose={() => setNavOpen(false)} />
        {navOpen && (
          <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setNavOpen(false)} aria-hidden="true" />
        )}
        <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
          {impersonating && (
            <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-amber-50 dark:bg-amber-500/10 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 shrink-0">
              <div className="flex items-center gap-2 text-amber-800 dark:text-amber-300">
                <AlertTriangle size={14} className="text-amber-500 shrink-0" />
                <span className="text-xs font-medium">
                  You are viewing as <span className="font-bold">{impersonating.name}</span>
                </span>
              </div>
              <button
                onClick={handleExitImpersonation}
                className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100 bg-amber-100 dark:bg-amber-900/40 hover:bg-amber-200 dark:hover:bg-amber-900/60 px-2.5 py-1 rounded-md transition-colors"
              >
                <X size={12} />
                Exit Impersonation
              </button>
            </div>
          )}
          <Topbar onMenu={() => setNavOpen(true)} menuOpen={navOpen} />
          <main className="flex-1 overflow-y-auto">
            <div className="p-4 sm:p-6 animate-fade-in">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </ToastProvider>
  );
}
